import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import useEmpDetails from "../../../core/api/useEmpdetail";
import { API_BASE_PATH } from "../../../environment";
import { RootState } from "../../../core/data/redux/store";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";

type PasswordField =
  | "oldPassword"
  | "newPassword"
  | "confirmPassword"
  | "currentPassword";

const Profile = () => {
  const route = all_routes;
  const empToken = useAuthToken();
  const employeeId = useSelector((state: RootState) => state.auth.employeeId);
  const role = useSelector((state: RootState) => state.auth.role);
  const navigate = useNavigate();
  const [passwordVisibility, setPasswordVisibility] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
    currentPassword: false,
  });

  const { data, isLoading, fetchDetails, updateProfile } = useEmpDetails(empToken);
  const [formValues, setFormValues] = useState<any>({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    street_address: "",
    area: "",
    city_name: "",
    country: "",
    state_name: "",
    zip_code: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (employeeId) {
      fetchDetails(employeeId, empToken);
    } else {
      console.warn("No employeeId found in Redux. Redirecting to login...");
      navigate("/login");
    }
  }, [employeeId]);

  useEffect(() => {
    if (data) {
      setFormValues({
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        phone: data.phone,
        street_address: data.street_address,
        area: data.area,
        city_name: data.city_name,
        country: data.country,
        state_name: data.state_name,
        zip_code: data.zip_code,
      });
    }
  }, [data]);

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [profileImageUrl, setProfileImageUrl] = useState<string>("");

  // Fetch profile image with authentication header
  useEffect(() => {
    if (employeeId && empToken) {
      fetchProfileImage();
    }
  }, [employeeId, empToken]);

  const fetchProfileImage = async () => {
    try {
      const timestamp = Date.now();
      const response = await fetch(
        `${API_BASE_PATH}/register/employee/${employeeId}/image?t=${timestamp}`,
        {
          headers: {
            Authorization: `Bearer ${empToken}`,
            Accept: 'image/*',
          },
        }
      );
      
      if (response.ok) {
        const imageBlob = await response.blob();
        const imageUrl = URL.createObjectURL(imageBlob);
        setProfileImageUrl(imageUrl);
      } else {
        // If image fetch fails, use default image
        setProfileImageUrl("");
      }
    } catch (error) {
      console.error("Error fetching profile image:", error);
      setProfileImageUrl("");
    }
  };

  // Cleanup image URL on component unmount
  useEffect(() => {
    return () => {
      if (profileImageUrl) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
  }, [profileImageUrl]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Handle back button navigation based on role
  const handleBackToDashboard = () => {
    if (!role) {
      // Fallback to employee dashboard if role is null/undefined
      navigate(all_routes.employeeDashboard);
      return;
    }

    if (role === "Superadmin") {
      navigate(all_routes.superAdminDashboard);
    } else if (["Admin", "Manager"].includes(role)) {
      navigate(all_routes.adminDashboard);
    } else {
      navigate(all_routes.employeeDashboard);
    }
  };

  const handleSave = async () => {
    const formData = new FormData();
    if (employeeId) {
      const currentPassword = formValues.currentPassword || "";
      const newPassword = formValues.newPassword || "";
      const { confirmPassword, ...updatedDetails } = formValues;

      if (profileImage) {
        formData.append("photo", profileImage);
      }
      if (newPassword && newPassword !== confirmPassword) {
        toast.warning("New password and confirm password do not match.");
        return;
      }
      if (newPassword && !currentPassword) {
        toast.warning("Please enter your current password to set a new one.");
        return;
      }

      try {
        await updateProfile(
          employeeId,
          empToken,
          {
            ...updatedDetails,
            ...(profileImage ? { photo: profileImage } : {}),
          },
          currentPassword,
          newPassword
        );

        toast.success("Profile updated successfully");

        // Optionally reset password fields
        setFormValues((prev: any) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));

        // Refresh profile image after successful update
        await fetchProfileImage();
        setImagePreview("");
        setProfileImage(null);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleCancel = () => {
    if (data) {
      setFormValues({
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        phone: data.phone,
        street_address: data.street_address,
        area: data.area,
        city_name: data.city_name,
        country: data.country,
        state_name: data.state_name,
        zip_code: data.zip_code,
      });
    }
    // Navigate back to dashboard on cancel
    handleBackToDashboard();
  };

  if (!employeeId || isLoading) {
    return <div>Loading...</div>;
  }

  if (!data) {
    return <div>No employee data found.</div>;
  }

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Profile </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="">
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Profile{" "}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleBackToDashboard}
                title="Back to Dashboard"
              >
                <i className="ti ti-arrow-left me-1"></i>
                Back
              </button>
              <CollapseHeader />
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="card">
            <div className="card-body">
              <div className="border-bottom mb-3 pb-3">
                <h4>Profile </h4>
              </div>
              <form action="profile.html">
                <div className="border-bottom mb-3">
                  <div className="row">
                    <div className="col-md-12">
                      <div>
                        <h6 className="mb-3">Basic Information</h6>
                        <div className="d-flex align-items-center flex-wrap row-gap-3 bg-light w-100 rounded p-3 mb-4">
                          <div
                            className="d-flex align-items-center justify-content-center avatar avatar-xxl rounded-circle border border-dashed me-2 flex-shrink-0 text-dark frames"
                            style={{
                              width: "80px",
                              height: "80px",
                              overflow: "hidden",
                              borderRadius: "50%",
                            }}
                          >
                            {imagePreview ? (
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="img-fluid rounded-circle"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            ) : profileImageUrl ? (
                              <img
                                src={profileImageUrl}
                                alt="Profile"
                                className="img-fluid rounded-circle"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            ) : (
                              <img
                                src="/assets/img/defaultavatar.png"
                                alt="Default Avatar"
                                className="img-fluid rounded-circle"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            )}
                          </div>
                          <div className="profile-upload">
                            <div className="mb-2">
                              <h6 className="mb-1">Upload Profile Image</h6>
                              <p className="fs-12">
                                Image should be below 4 MB
                              </p>
                            </div>
                            <div className="profile-uploader d-flex align-items-center">
                              <div className="drag-upload-btn btn btn-sm btn-primary me-2">
                                Upload
                                <input
                                  type="file"
                                  className="form-control image-sign"
                                  accept="image/*"
                                  onChange={handleImageChange}
                                />
                              </div>
                              {(profileImage || imagePreview) && (
                                <button
                                  type="button"
                                  className="btn btn-light btn-sm"
                                  onClick={() => {
                                    setProfileImage(null);
                                    setImagePreview("");
                                  }}
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">
                            First Name
                          </label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            name="firstname"
                            value={formValues.firstname}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">
                            Last Name
                          </label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            name="lastname"
                            value={formValues.lastname}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">Email</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            name="email"
                            value={formValues.email}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">Phone</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            name="phone"
                            value={formValues.phone}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-bottom mb-3">
                  <h6 className="mb-3">Address Information</h6>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-2">
                          <label className="form-label mb-md-0">Address</label>
                        </div>
                        <div className="col-md-10">
                          <input
                            type="text"
                            name="street_address"
                            className="form-control"
                            value={formValues.street_address}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">Country</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            name="country"
                            value={formValues.country}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">State</label>
                        </div>
                        <div className="col-md-8">
                          <div>
                            <input
                              type="text"
                              className="form-control"
                              name="state_name"
                              value={formValues.state_name}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">City</label>
                        </div>
                        <div className="col-md-8">
                          <div>
                            <input
                              type="text"
                              className="form-control"
                              name="city_name"
                              value={formValues.city_name}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">
                            Postal Code
                          </label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            className="form-control"
                            name="zip_code"
                            value={formValues.zip_code}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-bottom mb-3">
                  <h6 className="mb-3">Change Password</h6>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-5">
                          <label className="form-label mb-md-0">
                            Current Password
                          </label>
                        </div>
                        <div className="col-md-7">
                          <div className="pass-group">
                            <input
                              type={
                                passwordVisibility.currentPassword
                                  ? "text"
                                  : "password"
                              }
                              className="pass-input form-control"
                              name="currentPassword"
                              value={formValues.currentPassword || ""}
                              onChange={handleChange}
                            />
                            <span
                              className={`ti toggle-passwords ${
                                passwordVisibility.currentPassword
                                  ? "ti-eye"
                                  : "ti-eye-off"
                              }`}
                              onClick={() =>
                                togglePasswordVisibility("currentPassword")
                              }
                            ></span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-5">
                          <label className="form-label mb-md-0">
                            New Password
                          </label>
                        </div>
                        <div className="col-md-7">
                          <div className="pass-group">
                            <input
                              type={
                                passwordVisibility.newPassword
                                  ? "text"
                                  : "password"
                              }
                              className="pass-input form-control"
                              name="newPassword"
                              value={formValues.newPassword || ""}
                              onChange={handleChange}
                            />
                            <span
                              className={`ti toggle-passwords ${
                                passwordVisibility.newPassword
                                  ? "ti-eye"
                                  : "ti-eye-off"
                              }`}
                              onClick={() =>
                                togglePasswordVisibility("newPassword")
                              }
                            ></span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-5">
                          <label className="form-label mb-md-0">
                            Confirm Password
                          </label>
                        </div>
                        <div className="col-md-7">
                          <div className="pass-group">
                            <input
                              type={
                                passwordVisibility.confirmPassword
                                  ? "text"
                                  : "password"
                              }
                              className="pass-input form-control"
                              name="confirmPassword"
                              value={formValues.confirmPassword || ""}
                              onChange={handleChange}
                            />
                            <span
                              className={`ti toggle-passwords ${
                                passwordVisibility.confirmPassword
                                  ? "ti-eye"
                                  : "ti-eye-off"
                              }`}
                              onClick={() =>
                                togglePasswordVisibility("confirmPassword")
                              }
                            ></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-light border me-3"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed &amp; Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default Profile;